/**
 * @file IP Scoring System Utilities
 * @description Provides utility functions for calculating Investigation Points (IP) scores
 *
 * IMPORTANT: Wrong clicks have NO direct score penalty in the IP system.
 * They only affect score indirectly through:
 * 1. Breaking speed streaks
 * 2. Increasing cycle time (reducing time-efficiency points for next find)
 * 3. Lowering accuracy multiplier (0.5× to 1.5× - a reward reduction, not a penalty)
 * 4. Time penalty (-3 seconds from timer)
 */

import {
  ACCURACY_MULTIPLIER_CURVE,
  CASE_PINS_CONFIG,
  COMMENDATION_TIERS,
  DEFAULT_TIME_CUTOFFS,
  DEFAULT_TIME_TARGETS,
  EVIDENCE_BASE_POINTS,
  PACE_MULTIPLIER_CONFIG,
  PERSONAL_BEST_STORAGE_KEY,
  SPEED_STREAK_THRESHOLDS,
  TIME_EFFICIENCY_CURVE,
} from '../constants/ipScoringConfig';
import {
  type CommendationBadgeInfo,
  CommendationTier,
  type EvidenceFind,
  type IPScoreBreakdown,
  type PersonalBestData,
} from '../types/scoring';

/**
 * Calculates the base time efficiency bonus using continuous attenuation curve
 * @param timeTakenSeconds Time taken to find the evidence (in seconds)
 * @param _legacyStreakTimeThreshold Deprecated parameter (kept for backward compatibility)
 * @returns Bonus points earned (always an integer, rounded from continuous curve)
 *
 * Formula: maxPoints × 1 / (1 + (t / tHalf)^gamma)
 * From BriefScoringSystemV2 spec - continuous curve to maximize leaderboard granularity
 */
export function getBaseTimeEfficiencyBonus(
  timeTakenSeconds: number,
  _legacyStreakTimeThreshold?: number, // Deprecated parameter, kept for backward compatibility
): number {
  const { maxPoints, tHalf, gamma } = TIME_EFFICIENCY_CURVE;

  // Continuous attenuation formula from BriefScoringSystemV2
  // time_points(t) = maxPoints × 1 / (1 + (t / tHalf)^gamma)
  const rawBonus = maxPoints * (1 / (1 + (timeTakenSeconds / tHalf) ** gamma));

  // IMPORTANT: Always round to integer (no decimals)
  // This ensures consistent integer scoring throughout the system
  const bonus = Math.round(rawBonus);

  return bonus;
}

/**
 * Gets the speed streak multiplier for a given streak length
 * Streak 1 gets 1.0×, streak 2 gets 1.6×, etc.
 * @param streakLength The length of the speed streak (1-indexed)
 * @returns Multiplier value (1.0 for streak <= 0)
 */
export function getStreakMultiplier(streakLength: number): number {
  if (streakLength <= 0) return 1.0;

  // Find the threshold for this streak length (cap at max defined threshold)
  const threshold = SPEED_STREAK_THRESHOLDS.filter(
    (t) => streakLength >= t.minConsecutiveBonuses,
  ).sort((a, b) => b.multiplier - a.multiplier)[0];

  return threshold?.multiplier ?? 1.0;
}

/**
 * Calculates the total time-efficiency points with per-find speed streak multipliers
 * Each find in a streak gets the multiplier for its position:
 * - Find 1: 1.0×
 * - Find 2: 1.6×
 * - Find 3: 2.1×
 * - Find 4: 2.6×
 * - Find 5+: 3.1×
 *
 * @param finds Array of evidence finds
 * @param wrongClickTimestamps Array of timestamps when wrong clicks occurred
 * @returns Total time-efficiency points (with multipliers applied per-find)
 *
 * Speed streaks are broken by:
 * 1. A slow find (time taken > tHalf threshold)
 * 2. Any wrong click occurring between consecutive finds
 */
export function calculateTimeEfficiencyWithStreaks(
  finds: EvidenceFind[],
  wrongClickTimestamps: number[] = [],
): number {
  if (finds.length === 0) return 0;

  // Sort finds by timeWhenFound
  const sortedFinds = [...finds].sort(
    (a, b) => a.timeWhenFound - b.timeWhenFound,
  );

  // Sort wrong clicks for efficient lookup
  const sortedWrongClicks = [...wrongClickTimestamps].sort((a, b) => a - b);

  // Define minimum threshold for "fast" finds that contribute to streak
  // Using tHalf (2.0s) as the cutoff: finds faster than 2s are considered "fast"
  const FAST_FIND_THRESHOLD = TIME_EFFICIENCY_CURVE.tHalf;

  let totalPoints = 0;
  let currentStreakLength = 0;

  for (let i = 0; i < sortedFinds.length; i++) {
    const find = sortedFinds[i];

    // Check if any wrong click occurred between this find and the previous find
    const prevFindTime =
      i > 0 ? sortedFinds[i - 1].timeWhenFound : find.timeWhenFound;
    const wrongClickBetweenFinds = sortedWrongClicks.some(
      (timestamp) => timestamp > prevFindTime && timestamp < find.timeWhenFound,
    );

    // Reset streak if wrong click occurred between finds
    if (wrongClickBetweenFinds) {
      currentStreakLength = 0;
    }

    // Check if this find qualifies as "fast" for streak purposes
    if (find.timeTakenSeconds <= FAST_FIND_THRESHOLD) {
      // Fast find - increment streak and apply multiplier for this position
      currentStreakLength++;
      const multiplier = getStreakMultiplier(currentStreakLength);
      const pointsForThisFind = Math.round(
        find.baseTimeEfficiencyBonus * multiplier,
      );
      totalPoints += pointsForThisFind;
    } else {
      // Slow find - reset streak, add base points only (1.0× multiplier)
      currentStreakLength = 0;
      totalPoints += find.baseTimeEfficiencyBonus;
    }
  }

  return totalPoints;
}

/**
 * Gets the highest speed streak length achieved during the run
 * @param finds Array of evidence finds
 * @param wrongClickTimestamps Array of timestamps when wrong clicks occurred
 * @returns Highest streak length achieved
 */
export function getHighestSpeedStreak(
  finds: EvidenceFind[],
  wrongClickTimestamps: number[] = [],
): number {
  if (finds.length === 0) return 0;

  // Sort finds by timeWhenFound
  const sortedFinds = [...finds].sort(
    (a, b) => a.timeWhenFound - b.timeWhenFound,
  );

  // Sort wrong clicks for efficient lookup
  const sortedWrongClicks = [...wrongClickTimestamps].sort((a, b) => a - b);

  const FAST_FIND_THRESHOLD = TIME_EFFICIENCY_CURVE.tHalf;

  let highestStreak = 0;
  let currentStreakLength = 0;

  for (let i = 0; i < sortedFinds.length; i++) {
    const find = sortedFinds[i];

    // Check if any wrong click occurred between this find and the previous find
    const prevFindTime =
      i > 0 ? sortedFinds[i - 1].timeWhenFound : find.timeWhenFound;
    const wrongClickBetweenFinds = sortedWrongClicks.some(
      (timestamp) => timestamp > prevFindTime && timestamp < find.timeWhenFound,
    );

    // Reset streak if wrong click occurred between finds
    if (wrongClickBetweenFinds) {
      currentStreakLength = 0;
    }

    // Check if this find qualifies as "fast" for streak purposes
    if (find.timeTakenSeconds <= FAST_FIND_THRESHOLD) {
      currentStreakLength++;
      highestStreak = Math.max(highestStreak, currentStreakLength);
    } else {
      currentStreakLength = 0;
    }
  }

  return highestStreak;
}

/**
 * Calculates the accuracy multiplier using continuous power attenuation curve
 * @param correctClicks Number of correct clicks
 * @param wrongClicks Number of wrong clicks
 * @returns Multiplier value (rounded to 2 decimal places)
 *
 * Formula (two-step):
 * 1. adjusted_accuracy = (correct + α) / (correct + λ·wrong + α + β)
 * 2. multiplier = minAcc + (maxAcc − minAcc) × (adjusted_accuracy^γ)
 *
 * From BriefScoringSystemV2 spec - continuous curve to maximize leaderboard granularity
 */
export function calculateAccuracyMultiplier(
  correctClicks: number,
  wrongClicks: number,
): number {
  const { minAcc, maxAcc, gamma, lambda, alpha, beta } =
    ACCURACY_MULTIPLIER_CURVE;

  // Step 1: Calculate adjusted accuracy with smoothing factors
  // adjusted_accuracy = (correct + α) / (correct + λ·wrong + α + β)
  const numerator = correctClicks + alpha;
  const denominator = correctClicks + lambda * wrongClicks + alpha + beta;
  const adjustedAccuracy = numerator / denominator;

  // Step 2: Apply power curve to get multiplier
  // multiplier = minAcc + (maxAcc − minAcc) × (adjusted_accuracy^γ)
  const multiplierRange = maxAcc - minAcc;
  const rawMultiplier = minAcc + multiplierRange * adjustedAccuracy ** gamma;

  // Round to 2 decimal places for display consistency
  const multiplier = Math.round(rawMultiplier * 100) / 100;

  return multiplier;
}

/**
 * Calculates the pace multiplier based on Total Completion Time
 * @param totalCompletionTimeSec Total time from first to last find (seconds)
 * @param parTime Solid run target time (seconds)
 * @param aceTime Top-tier run target time (seconds)
 * @returns Pace multiplier value (0.95 to 1.10)
 */
export function calculatePaceMultiplier(
  totalCompletionTimeSec: number,
  parTime: number = DEFAULT_TIME_TARGETS.parTime,
  aceTime: number = DEFAULT_TIME_TARGETS.aceTime,
): number {
  if (!PACE_MULTIPLIER_CONFIG.enabled) {
    return 1.0;
  }

  const { paceMin, paceMax, gamma } = PACE_MULTIPLIER_CONFIG;

  // p = clamp(0, 1, (ParTime - T) / (ParTime - AceTime))
  const numerator = parTime - totalCompletionTimeSec;
  const denominator = parTime - aceTime;
  const p = Math.max(0, Math.min(1, numerator / denominator));

  // paceMultiplier = paceMin + (paceMax - paceMin) * (p ^ gamma)
  const multiplier = paceMin + (paceMax - paceMin) * p ** gamma;

  return Math.round(multiplier * 100) / 100;
}

/**
 * Calculates Case Pins (0-5) based on Total Completion Time
 * @param totalCompletionTimeSec Total time from first to last find (seconds)
 * @param parTime Solid run target time (seconds)
 * @param aceTime Top-tier run target time (seconds)
 * @returns Pin count (0-5)
 */
export function calculateCasePins(
  totalCompletionTimeSec: number,
  parTime: number = DEFAULT_TIME_TARGETS.parTime,
  aceTime: number = DEFAULT_TIME_TARGETS.aceTime,
): number {
  const { maxPins, gamma } = CASE_PINS_CONFIG;

  // p = clamp(0, 1, (ParTime - T) / (ParTime - AceTime))
  const numerator = parTime - totalCompletionTimeSec;
  const denominator = parTime - aceTime;
  const p = Math.max(0, Math.min(1, numerator / denominator));

  // pins = round(5 * p ^ gamma)
  const pins = Math.round(maxPins * p ** gamma);

  return pins;
}

/**
 * Calculates Commendation Badge tier based on Total Completion Time
 * Uses stub cutoffs until real leaderboard data is available
 * @param totalCompletionTimeSec Total time from first to last find (seconds)
 * @param timeCutoffs Optional custom time cutoffs for this case
 * @returns Commendation badge info
 */
export function calculateCommendationBadge(
  totalCompletionTimeSec: number,
  timeCutoffs: typeof DEFAULT_TIME_CUTOFFS = DEFAULT_TIME_CUTOFFS,
): CommendationBadgeInfo {
  let tier: CommendationTier;

  if (totalCompletionTimeSec <= timeCutoffs.t01) {
    tier = CommendationTier.CHIEF_INSPECTOR;
  } else if (totalCompletionTimeSec <= timeCutoffs.t10) {
    tier = CommendationTier.INSPECTOR;
  } else if (totalCompletionTimeSec <= timeCutoffs.t20) {
    tier = CommendationTier.DETECTIVE_GRADE_I;
  } else if (totalCompletionTimeSec <= timeCutoffs.t40) {
    tier = CommendationTier.DETECTIVE_GRADE_II;
  } else if (totalCompletionTimeSec <= timeCutoffs.t60) {
    tier = CommendationTier.DETECTIVE_GRADE_III;
  } else {
    tier = CommendationTier.NONE;
  }

  const tierInfo = COMMENDATION_TIERS[tier];

  return {
    tier,
    name: tierInfo.name,
    percentile: tierInfo.percentile,
    description: tierInfo.description,
  };
}

/**
 * Gets personal best data from localStorage
 * @returns Personal best data
 */
export function getPersonalBestData(): PersonalBestData {
  const storedData = localStorage.getItem(PERSONAL_BEST_STORAGE_KEY);

  if (!storedData) {
    // Initialize with empty data
    return {
      perCase: {},
      globalHighScore: 0,
    };
  }

  try {
    return JSON.parse(storedData) as PersonalBestData;
  } catch (error) {
    console.error('Error parsing personal best data:', error);
    return {
      perCase: {},
      globalHighScore: 0,
    };
  }
}

/**
 * Updates personal best data and returns whether new records were set
 * @param caseId Case ID
 * @param score Final score
 * @returns Object indicating if new records were set
 */
export function updatePersonalBest(
  caseId: string,
  score: number,
): { isNewPersonalBest: boolean; isNewGlobalBest: boolean } {
  const personalBestData = getPersonalBestData();

  // Check if this is a new personal best for this case
  const currentCaseBest = personalBestData.perCase[caseId] || 0;
  const isNewPersonalBest = score > currentCaseBest;

  // Check if this is a new global best
  const isNewGlobalBest = score > personalBestData.globalHighScore;

  // Update data if new records were set
  if (isNewPersonalBest || isNewGlobalBest) {
    if (isNewPersonalBest) {
      personalBestData.perCase[caseId] = score;
    }

    if (isNewGlobalBest) {
      personalBestData.globalHighScore = score;
    }

    // Save updated data
    localStorage.setItem(
      PERSONAL_BEST_STORAGE_KEY,
      JSON.stringify(personalBestData),
    );
  }

  return { isNewPersonalBest, isNewGlobalBest };
}

/**
 * Calculates the complete IP score breakdown
 *
 * Scoring Flow (per spec):
 * 1. Evidence Points: 100 × items found
 * 2. Time-Efficiency Points: Per-find with speed streak multipliers applied
 *    - Each find gets multiplier based on its position in streak: [1.0, 1.6, 2.1, 2.6, 3.1]
 * 3. Subtotal = Evidence Points + Time-Efficiency Points
 * 4. Apply Accuracy Multiplier (continuous curve, 0.5× to 1.5×)
 * 5. Apply Pace Multiplier (continuous curve, 0.95× to 1.10×)
 * 6. Final IP = result after both multipliers
 *
 * @param finds Array of evidence finds
 * @param wrongClickTimestamps Array of timestamps when wrong clicks occurred
 * @param requiredEvidenceCount Number of evidence items required (unused in non-timed mode)
 * @param caseId Case ID (for personal best tracking)
 * @param totalCompletionTime Total time from first to last find (seconds)
 * @param parTime Optional par time for pace multiplier (seconds)
 * @param aceTime Optional ace time for pace multiplier (seconds)
 * @returns Complete IP score breakdown
 */
export function calculateIPScore(
  finds: EvidenceFind[],
  wrongClickTimestamps: number[],
  _requiredEvidenceCount: number,
  caseId: string,
  totalCompletionTime: number,
  parTime?: number,
  aceTime?: number,
): IPScoreBreakdown {
  // 1. Calculate evidence points (100 per evidence)
  const evidencePoints = finds.length * EVIDENCE_BASE_POINTS;

  // 2. Calculate time-efficiency points with per-find speed streak multipliers
  // Each find in a streak gets its position's multiplier: [1.0, 1.6, 2.1, 2.6, 3.1]
  const timeEfficiencyPoints = calculateTimeEfficiencyWithStreaks(
    finds,
    wrongClickTimestamps,
  );

  // 3. Calculate subtotal (before accuracy multiplier)
  // Note: No accuracy streak bonuses or threshold bonus in non-timed mode (per spec)
  const subtotal = evidencePoints + timeEfficiencyPoints;

  // 4. Get accuracy multiplier (using continuous curve)
  const correctClicks = finds.length;
  const wrongClicks = wrongClickTimestamps.length;
  const accuracyMultiplier = calculateAccuracyMultiplier(
    correctClicks,
    wrongClicks,
  );

  // Calculate score after accuracy multiplier
  const scoreAfterAccuracy = Math.round(subtotal * accuracyMultiplier);

  // 5. Calculate and apply pace multiplier
  const paceMultiplier = calculatePaceMultiplier(
    totalCompletionTime,
    parTime,
    aceTime,
  );
  const scoreAfterPace = Math.round(scoreAfterAccuracy * paceMultiplier);

  // Check for personal best (using score after pace)
  const { isNewPersonalBest: _isNewPersonalBest } = updatePersonalBest(
    caseId,
    scoreAfterPace,
  );

  // 6. Final score (personal best bonus disabled per spec)
  const finalScore = scoreAfterPace;

  // Get highest streak for display purposes
  const highestStreak = getHighestSpeedStreak(finds, wrongClickTimestamps);

  return {
    evidencePoints,
    timeEfficiencyPoints,
    highestSpeedStreak: highestStreak,
    subtotal,
    accuracyMultiplier,
    paceMultiplier,
    scoreBeforePace: scoreAfterAccuracy,
    totalCompletionTime,
    finalScore,
  };
}
